/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from "three";

import { const_var, params } from '../../../../../redux/reducers/reducer';
import { extrudeSettings, railModel } from '../../../../../utils/Functions';
import BaseRailModel from '../../BaseRail';
import PillarRailModel from '../../Legs';
import CommonTruss from '../CommonTruss';
import { MaterialTruss } from '../../../CustomMaterial';
import { trussDimensions } from '../../../../../helpers/building_component/truss/truss.helper';

const roofRailDistanceY = 1;
const hangRailDivision = 3 / 5;

const ACT = ({ trussType, normalMap, envMap, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, meshDelModel_CSG, isFor  }) => {

    return (
        <>
            {isFor == 'center' && <BaseRailModel normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} />}
            {isFor == 'center' && <PillarRailModel trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} roofRailDistanceY={roofRailDistanceY} hangRailDivision={hangRailDivision} />}
            <TrussModel trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} isFor={isFor} />
        </>
    )
}

export default ACT

const TrussModel = ({ trussType, trussModelPosInfoArr, normalMap, envMap, overlapTrussArr, hiddenBaseRailIdArr, isFor }) => {
    const obj_centerVRail = new THREE.Object3D();
    const obj_centerHRail = new THREE.Object3D();
    const obj_connectVRail = new THREE.Object3D();
    const obj_connectSRail = new THREE.Object3D();
    const obj_underTruss = new THREE.Object3D();
    const vector = new THREE.Vector3(1, 0, 0)

    const ref_centerVRail = useRef();
    const ref_centerHRail = useRef();
    const ref_connectVRail = useRef();
    const ref_connectSRail  = useRef();
    const ref_underTruss = useRef();

    const bottomBaseRailWidth = 0.3;
    const bottomBaseRailHeight = 0.1;
    const connectRailWidth = 0.3;
    const connectRailHeight = 0.2;
    const offset = 0.035;
    const trussDimension = trussDimensions(isFor)

    const alignPosOffset = useMemo(() => {
        if(params.p_r_s === 1 ) return const_var.roofRegularDst * 2;
        else if(params.p_r_s === 2 || params.p_r_s === 3) return 0;
        else return 0;
    }, [params.p_r_s, trussDimension.roofPitch])

    const roofAlpha = Math.atan(trussDimension.roofPitch / 12);
    const roofHeight = (trussDimension.buildingWidth / 2) * (trussDimension.roofPitch / 12);
    const bottomBaseRailLength = (trussDimension.buildingWidth - bottomBaseRailHeight / 2 - connectRailWidth / 2) * hangRailDivision;
    const roofCenterRailHeight = ((trussDimension.buildingWidth / 2) * trussDimension.roofPitch / 12) * hangRailDivision + roofRailDistanceY;
    const roofOuterRailLength = useMemo(() => (Math.sqrt(Math.pow(trussDimension.buildingWidth / 2, 2) + Math.pow(roofHeight, 2))), [trussDimension.buildingWidth, roofHeight])
    
    const connectRailDataArr = useMemo(() => {
        let dataInfoArray = [];
        let count, distance;

        let initDistance = 2;
        const tempCount = Math.floor((trussDimension.buildingWidth / 2) / initDistance);
        if ((trussDimension.buildingWidth / 2) === tempCount * initDistance) {
            count = tempCount;
            distance = initDistance;
        } else if ((trussDimension.buildingWidth / 2) - tempCount * initDistance <= initDistance / 2) {
            count = tempCount;
            distance = (trussDimension.buildingWidth / 2) / count;
        } else {
            count = tempCount + 1;
            distance = (trussDimension.buildingWidth / 2) / count;
        }

        let slopeRailLength = 0;
        let verticalRailLength = roofRailDistanceY;
        
        for (let i = 1; i <= count; i++) {
            const pos_x = distance * i - connectRailWidth / 2;
            const pos_y = trussDimension.buildingHeight + (trussDimension.buildingWidth / 2 - pos_x) * (trussDimension.roofPitch / 12) - roofRailDistanceY + 0.03;
            slopeRailLength = Math.sqrt(Math.pow(distance, 2) + Math.pow((roofRailDistanceY + distance * Math.tan(roofAlpha)), 2)) - 0.05;
            const alpha = Math.asin(distance / slopeRailLength);
            
            const tempInfoObj = {
                distance: distance,
                pos_x: pos_x,
                pos_y: pos_y,
                s_length: slopeRailLength,
                v_length: (params.p_r_s === 1 && i === count ) ? 0 : roofRailDistanceY,
                alpha: alpha
            }

            dataInfoArray.push(tempInfoObj);
        }

        return {
            slopeRailLength,
            verticalRailLength,
            dataInfoArray
        }
    }, [trussDimension.buildingHeight, trussDimension.buildingWidth, roofAlpha, trussDimension.roofPitch])

    useEffect(() => {
        if (ref_centerHRail.current && ref_centerVRail.current && ref_connectVRail.current && ref_connectSRail.current && ref_underTruss.current) {
            let index_all_1 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 1) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    //----- roof under rail-----
                    obj_underTruss.position.set(offsetForDir * (-trussDimension.buildingWidth / 2 + connectRailWidth / 4), trussDimension.buildingHeight - connectRailHeight / 2 - roofRailDistanceY + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_underTruss.rotation.y = offsetForDir * Math.PI / 2;
                    obj_underTruss.rotateOnAxis(vector, offsetForDir * roofAlpha);
                    //--------------------------

                    //----- bottom base rail-----
                    obj_centerHRail.position.set(-bottomBaseRailLength / 2, trussDimension.buildingHeight + roofHeight - roofCenterRailHeight - bottomBaseRailHeight / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerHRail.rotation.y = Math.PI / 2;
                    //---------------------

                    //----- roof center rail-----
                    obj_centerVRail.position.set(0, trussDimension.buildingHeight + roofHeight - roofCenterRailHeight - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerVRail.rotation.x = -Math.PI / 2;

                    //---------------------

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_underTruss.scale.setScalar(0);
                        obj_centerHRail.scale.setScalar(0);
                        obj_centerVRail.scale.setScalar(0);
                    } else {
                        obj_underTruss.scale.setScalar(1);
                        obj_centerHRail.scale.setScalar(1);
                        obj_centerVRail.scale.setScalar(1);
                    }
                    obj_underTruss.updateMatrix();
                    ref_underTruss.current.setMatrixAt(index_all_1, obj_underTruss.matrix);
                    obj_centerVRail.updateMatrix();
                    ref_centerVRail.current.setMatrixAt(index_all_1, obj_centerVRail.matrix);
                    obj_centerHRail.updateMatrix();
                    ref_centerHRail.current.setMatrixAt(index_all_1, obj_centerHRail.matrix);

                    index_all_1 ++;
                })
            })

            let index_all_2 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    new Array(connectRailDataArr.dataInfoArray.length).fill('').forEach((_, index_rail) => {
                        obj_connectVRail.position.set(offsetForDir * connectRailDataArr.dataInfoArray[index_rail].pos_x, connectRailDataArr.dataInfoArray[index_rail].pos_y - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_connectVRail.rotation.x = -Math.PI / 2;

                        obj_connectSRail.position.set(offsetForDir * connectRailDataArr.dataInfoArray[index_rail].pos_x, connectRailDataArr.dataInfoArray[index_rail].pos_y - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_connectSRail.rotation.y = offsetForDir * Math.PI / 2;
                        obj_connectSRail.rotateOnAxis(vector, offsetForDir * (-Math.PI / 2 - connectRailDataArr.dataInfoArray[index_rail].alpha));

                        if (hiddenBaseRailIdArr.includes(index_truss)) {
                            obj_connectVRail.scale.setScalar(0);
                            obj_connectSRail.scale.setScalar(0);
                        } else {
                            obj_connectVRail.scale.setScalar(1);
                            obj_connectSRail.scale.setScalar(1);
                        }

                        if (connectRailDataArr.dataInfoArray[index_rail].v_length === 0) obj_connectVRail.scale.setScalar(0);

                        obj_connectVRail.updateMatrix();
                        ref_connectVRail.current.setMatrixAt(index_all_2, obj_connectVRail.matrix);
                        obj_connectSRail.updateMatrix();
                        ref_connectSRail.current.setMatrixAt(index_all_2, obj_connectSRail.matrix);

                        index_all_2 ++;
                    })
                })
            })
            
            ref_centerHRail.current.instanceMatrix.needsUpdate = true;
            ref_centerVRail.current.instanceMatrix.needsUpdate = true;
            ref_connectVRail.current.instanceMatrix.needsUpdate = true;
            ref_connectSRail.current.instanceMatrix.needsUpdate = true;
            ref_underTruss.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_centerHRail.current, ref_centerVRail.current, ref_connectVRail.current, ref_connectSRail.current, ref_underTruss.current, trussDimension.buildingWidth, trussDimension.buildingHeight, trussModelPosInfoArr, hiddenBaseRailIdArr, connectRailDataArr, roofHeight, roofCenterRailHeight, alignPosOffset, roofAlpha])

    return (
        <group position={[0, -offset, 0]} rotation={[0,trussDimension.trussRotation,0]} name='ACT-truss-legs'>
            <CommonTruss trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} normalMap={normalMap} envMap={envMap} isShowSlopeRail={false}  isFor={isFor} />
            <>
                {/* --- roof unnder truss model --- */}
                <instancedMesh ref={ref_underTruss} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(roofOuterRailLength - 0.04)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- bottom base rail model --- */}
                <instancedMesh   instancedMesh ref={ref_centerHRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(bottomBaseRailWidth, bottomBaseRailHeight), extrudeSettings(bottomBaseRailLength)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- roof center rail model --- */}
                <instancedMesh ref={ref_centerVRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(roofCenterRailHeight)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- connect rail model --- */}
                <instancedMesh ref={ref_connectVRail} args={[null, null, trussModelPosInfoArr.length * connectRailDataArr.dataInfoArray.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(connectRailDataArr.verticalRailLength)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                <instancedMesh ref={ref_connectSRail} args={[null, null, trussModelPosInfoArr.length * connectRailDataArr.dataInfoArray.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(connectRailDataArr.slopeRailLength - 0.15)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}
            </>
        </group>
    )
}